import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { Download } from 'grommet-icons';
import { SignatureInput, AppButton } from '@Components/Control';
import {
  PrivacyPolicyModal, TermsOfUseModal, CidOnboardingTermsModal, ExhibitAModal,
} from '@Components/Modal';
import { useAppTheme } from '@Components/Context';
import {
  getCidPricing, getEarlyBirdCidSetupFee,
} from '../pricingHelpers';

const AgreementStep = ({
  small,
  submitLoading,
  offerings,
  accountKind,
  channels,
  uploader,
  cidPlan,
  socialPlatforms,
  pricingData = null,
  basicDetails,
  onSignatureSubmit,
  onSignatureReset,
  signatureSubmitted,
  signaturePreview = null,
  earlyBirdEligibility = null,
  finalSubmitted,
  estimatedTotal,
  cidPlanDisplayLabel,
  uploaderDisplayLabel,
  onDownloadAgreement = null,
  downloadingAgreement = false,
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showExhibitA, setShowExhibitA] = useState(false);

  const { theme: { page, button } } = useAppTheme();

  const cidPricing = getCidPricing(pricingData, uploader, cidPlan);
  const cidSetupFee = cidPricing?.setupFeeAmount ?? 0;
  const earlyBirdSetupFee = getEarlyBirdCidSetupFee(pricingData, uploader, cidPlan);
  const isEarlyBirdEligible = Boolean(earlyBirdEligibility?.eligible);
  const channelCount = channels.length;
  const effectiveSetupFee = (isEarlyBirdEligible && earlyBirdSetupFee != null
    ? earlyBirdSetupFee
    : cidSetupFee) * channelCount;
  const cidRevenueShare = cidPricing?.revenueSharePercent ?? null;
  const currency = pricingData?.currency ?? 'USD';
  const effectiveDate = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const platforms = socialPlatforms || [];

  const getHybridDetails = React.useCallback((uploaderKey) => {
    const configValue = pricingData?.rules?.youtubeCid?.[uploaderKey]?.hybrid;
    return {
      initialMonths: configValue?.initialMonths ?? 3,
      initialSharePercent: configValue?.initialSharePercent ?? 0,
      afterSharePercent: configValue?.afterSharePercent ?? 10,
    };
  }, [pricingData]);

  const isDetailsValid = !!basicDetails.companyName
    && !!basicDetails.fullName
    && !!basicDetails.email;

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Agreement Acknowledgment</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Please review the agreement and provide your electronic signature to proceed.
        </Text>
      </Box>
      <Box
        gap="1rem"
        background={button.selectBg}
        round="0.5rem"
        pad="1.25rem"
        border={{ color: button.primaryBg, size: '1px' }}
      >
        <Text size="1.1rem" color={page.primaryText} style={{ lineHeight: '1.5' }}>
          By signing below, I acknowledge that I have reviewed and agree to
          {' '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowAgreement(true)}
          >
            Redflag&apos;s Content Protection Services Agreement
          </Text>
          , including
          {' '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowExhibitA(true)}
          >
            Exhibit A
          </Text>
          {' ('}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowTerms(true)}
          >
            Terms & Conditions
          </Text>
          {' and '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowPrivacy(true)}
          >
            Privacy Policy
          </Text>
          ).
        </Text>
        <Box gap="0.25rem" background={page.cardBg} round="var(--app-radius)" pad="1rem">
          <Text weight={600} size="1.1rem" color={page.primaryText}>
            Agreement Copy
          </Text>
          <Text size="0.95rem" color={page.secondaryText}>
            A signed copy of this agreement, including all exhibits and revenue terms,
            will be automatically sent to your provided email address after submission.
          </Text>
          {onDownloadAgreement && (
            <Box pad={{ top: '0.5rem' }}>
              <AppButton
                overrideHover
                level="tableControls"
                label={downloadingAgreement ? 'Generating...' : 'Download Agreement'}
                icon={!downloadingAgreement ? <Download size="small" /> : undefined}
                disabled={downloadingAgreement}
                bgColor={button.primaryBg}
                color={button.primaryText}
                onClick={onDownloadAgreement}
              />
            </Box>
          )}
        </Box>
      </Box>
      {showAgreement && (
        <CidOnboardingTermsModal
          toggle={() => setShowAgreement(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
          isAgency={accountKind === 'agency'}
        />
      )}
      {showTerms && (
        <TermsOfUseModal
          toggle={() => setShowTerms(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
        />
      )}
      {showPrivacy && (
        <PrivacyPolicyModal
          toggle={() => setShowPrivacy(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
        />
      )}
      {showExhibitA && (
        <ExhibitAModal
          toggle={() => setShowExhibitA(false)}
          isSmall={small}
          offerings={offerings}
          platforms={platforms}
          channels={channels}
          effectiveDate={effectiveDate}
          setupFee={effectiveSetupFee}
          monthlyFee={estimatedTotal}
          currency={currency}
          revenueSharePercent={cidRevenueShare}
          cidPlanDisplayLabel={cidPlanDisplayLabel}
          uploaderDisplayLabel={uploaderDisplayLabel}
          hybridDetails={cidPlan === 'B' ? getHybridDetails(uploader) : null}
          isHybrid={cidPlan === 'B'}
        />
      )}
      <SignatureInput
        small={small}
        submitLoading={submitLoading}
        signatureSubmitted={signatureSubmitted}
        signaturePreview={signaturePreview}
        finalSubmitted={finalSubmitted}
        isDetailsValid={isDetailsValid}
        onSignatureSubmit={onSignatureSubmit}
        onSignatureReset={onSignatureReset}
        uploadInputId="agreement-signature-upload-input"
      />
    </Box>
  );
};

AgreementStep.propTypes = {
  small: PropTypes.bool.isRequired,
  offerings: PropTypes.object.isRequired,
  accountKind: PropTypes.string.isRequired,
  channels: PropTypes.array.isRequired,
  uploader: PropTypes.string.isRequired,
  cidPlan: PropTypes.string.isRequired,
  socialPlatforms: PropTypes.array.isRequired,
  pricingData: PropTypes.shape({
    version: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    effectiveAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    rules: PropTypes.shape({
      youtubeCid: PropTypes.shape({
        self: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
        }).isRequired,
        redflag: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
            earlyBirdSetupFeeAmount: PropTypes.number,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      socialScanning: PropTypes.shape({
        perPlatformMonthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
      watermarking: PropTypes.shape({
        setupFeeAmount: PropTypes.number.isRequired,
        monthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
    display: PropTypes.shape({
      planLabels: PropTypes.shape({
        revenueShare: PropTypes.string.isRequired,
        hybrid: PropTypes.string.isRequired,
      }).isRequired,
      uploaderLabels: PropTypes.shape({
        self: PropTypes.string.isRequired,
        redflag: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }),
  basicDetails: PropTypes.object.isRequired,
  onSignatureSubmit: PropTypes.func.isRequired,
  onSignatureReset: PropTypes.func.isRequired,
  signatureSubmitted: PropTypes.bool.isRequired,
  signaturePreview: PropTypes.string,
  submitLoading: PropTypes.bool.isRequired,
  finalSubmitted: PropTypes.bool.isRequired,
  earlyBirdEligibility: PropTypes.shape({
    eligible: PropTypes.bool,
    expiresAt: PropTypes.string,
    remainingSeconds: PropTypes.number,
    reason: PropTypes.string,
  }),
  estimatedTotal: PropTypes.number,
  cidPlanDisplayLabel: PropTypes.string.isRequired,
  uploaderDisplayLabel: PropTypes.string.isRequired,
  onDownloadAgreement: PropTypes.func,
  downloadingAgreement: PropTypes.bool,
};

export default AgreementStep;
